import { c as _c } from "react/compiler-runtime";
import { FormikHelpers, useField, useFormikContext } from 'formik';
import { Dispatch, SetStateAction, useContext, useRef } from 'react';
import { OutpostDestination, OutpostDestinationCreateInput, OutpostDestinationTypeMetadata, OutpostDestinationUpdateInput } from '../../../../../../../typings/outpost';
import Button, { PlaceholderButton } from '../../../common/base/Button';
import Text from '../../../common/base/Text';
import MultiSelectInput from '../../../common/Form/Fields/MultiSelectInput';
import SelectInput from '../../../common/Form/Fields/SelectInput';
import SwitchInput from '../../../common/Form/Fields/SwitchInput';
import TextInput from '../../../common/Form/Fields/TextInput';
import { Div } from '../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { KeyValueEntry, KeyValueMapField, SetupLinkField, validateField } from '../Destinations/DestinationFields';
import { DestinationTypeIcon } from '../Destinations/DestinationTypeIcon';
export type MetadataEntry = [string, string];
export interface TenantFormValues {
  tenant_id: string;
  metadata_entries: MetadataEntry[];
}
export interface DestinationFormValues {
  type: string;
  all_topics: boolean;
  topics: string[];
  config: Record<string, string | KeyValueEntry[]>;
  credentials: Record<string, string>;
}
export interface DestinationTopicsFormValues {
  all_topics: boolean;
  topics: string[];
}
export interface DestinationConfigFormValues {
  config: Record<string, string | KeyValueEntry[]>;
  credentials: Record<string, string>;
}
interface HookdeckApiLike {
  outpost: {
    tenants: {
      tenantIdIsUsed: (tenant_id: string) => Promise<boolean>;
      update: (tenant_id: string, input: {
        metadata: Record<string, unknown>;
      }) => Promise<unknown>;
    };
    destinations: {
      create: (tenant_id: string, input: OutpostDestinationCreateInput) => Promise<unknown>;
    };
  };
}
const EMPTY_ENTRY: MetadataEntry = ['', ''];
const KeyValueEntriesField: React.FC<{
  name: string;
  add_label: string;
}> = ({
  name,
  add_label
}) => {
  const [{
    value
  },, {
    setValue
  }] = useField<MetadataEntry[]>(name);
  const render_key_ref = useRef(new Date().getTime());
  const entries = Array.isArray(value) && value.length > 0 ? value : [];
  const removeEntry = (index: number) => {
    const new_entries = [...entries];
    new_entries.splice(index, 1);
    setValue(new_entries.length === 0 ? [] : new_entries);
    render_key_ref.current = new Date().getTime();
  };
  const addEntry = () => {
    setValue([...entries, EMPTY_ENTRY]);
  };
  return <>
      {entries.map((_, index_0) => <Div key={`${render_key_ref.current}-${index_0}`} flex={{
      align: 'center',
      gap: 3
    }} m={{
      b: 2
    }}>
          <TextInput w={100} m={0} name={`${name}[${index_0}][0]`} placeholder="Key" mono />
          <TextInput w={100} m={0} name={`${name}[${index_0}][1]`} placeholder="Value" mono />
          <Button outline icon="delete" onClick={() => removeEntry(index_0)} />
        </Div>)}
      <PlaceholderButton m={{
      t: entries.length === 0 ? 0 : 1
    }} p={1.5} onClick={addEntry}>
        {add_label}
      </PlaceholderButton>
    </>;
};
export const MetadataFields = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <KeyValueEntriesField name="metadata_entries" add_label="Add metadata entry" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const TenantCreateFields = t0 => {
  const $ = _c(11);
  const {
    current_tenant_id,
    focus: t1
  } = t0;
  const focus = t1 === undefined ? false : t1;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t2;
  if ($[0] !== HookdeckAPI || $[1] !== current_tenant_id) {
    t2 = createTenantIdValidator({
      HookdeckAPI,
      current_tenant_id
    });
    $[0] = HookdeckAPI;
    $[1] = current_tenant_id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const validate_tenant_id = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      b: 4
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== focus || $[5] !== validate_tenant_id) {
    t4 = <TextInput name="tenant_id" label="Tenant ID" placeholder="my-tenant-id" required={true} focus={focus} mono={true} validate={validate_tenant_id} help="Bring your own tenant ID, the stable ID you use for that entity in your product. This ID identifies your tenant in API calls." m={t3} />;
    $[4] = focus;
    $[5] = validate_tenant_id;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Text semi={true} size="xs" muted={true} m={{
      b: 2
    }}>Metadata</Text>;
    t6 = <MetadataFields />;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4) {
    t7 = <>{t4}{t5}{t6}</>;
    $[9] = t4;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
};
export const metadataToEntries = (metadata: Record<string, unknown> | undefined): MetadataEntry[] => {
  if (!metadata || Object.keys(metadata).length === 0) {
    return [EMPTY_ENTRY];
  }
  return Object.entries(metadata).map(([key, value]) => [key, typeof value === 'object' ? JSON.stringify(value) : String(value)]);
};
export const entriesToMetadata = (entries: MetadataEntry[]): Record<string, unknown> => {
  const metadata: Record<string, unknown> = {};
  for (const [key, value] of entries) {
    if (!key.trim()) {
      continue;
    }
    try {
      metadata[key.trim()] = JSON.parse(value);
    } catch {
      metadata[key.trim()] = value;
    }
  }
  return metadata;
};
export const validateTenantId = (value: string) => {
  if (!value || value.trim().length === 0) {
    return 'Tenant ID is required';
  }
  if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
    return 'Tenant ID can only contain letters, numbers, underscores, and hyphens';
  }
  return undefined;
};
const createTenantIdValidator = ({
  HookdeckAPI,
  current_tenant_id
}: {
  HookdeckAPI: HookdeckApiLike;
  current_tenant_id?: string | null;
}) => {
  return async (value: string) => {
    const base_error = validateTenantId(value);
    if (base_error) {
      return base_error;
    }
    const is_used = await HookdeckAPI.outpost.tenants.tenantIdIsUsed(value);
    if (is_used && value !== current_tenant_id) {
      return 'A tenant with this ID already exists';
    }
    return undefined;
  };
};
export const getCreateTenantInitialValues = (tenant_id = ''): TenantFormValues => ({
  tenant_id,
  metadata_entries: []
});
export const getOutpostErrorMessage = (error: unknown) => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined;
  }
  const response = error.response;
  if (!response || typeof response !== 'object' || !('message' in response)) {
    return undefined;
  }
  return typeof response.message === 'string' ? response.message : undefined;
};
export const submitCreateTenantForm = ({
  HookdeckAPI,
  on_success,
  on_error
}: {
  HookdeckAPI: HookdeckApiLike;
  on_success?: () => void | Promise<void>;
  on_error?: (error_message?: string) => void;
}) => async (values: TenantFormValues, {
  setSubmitting
}: FormikHelpers<TenantFormValues>) => {
  try {
    const metadata = entriesToMetadata(values.metadata_entries);
    await HookdeckAPI.outpost.tenants.update(values.tenant_id, {
      metadata
    });
    await on_success?.();
  } catch (error: unknown) {
    on_error?.(getOutpostErrorMessage(error));
  } finally {
    setSubmitting(false);
  }
};
export const getTopicOptions = (topics_value: string | undefined) => (topics_value || '').split(',').map(topic => topic.trim()).filter(Boolean).map(topic => ({
  value: topic,
  label: topic
}));
export const getDestinationTopicsInitialValues = (topics: string[]): DestinationTopicsFormValues => {
  if (!topics || topics.length === 0) {
    return {
      all_topics: true,
      topics: []
    };
  }
  const is_all_topics = topics.length === 1 && topics[0] === '*';
  return {
    all_topics: is_all_topics,
    topics: is_all_topics ? [] : topics
  };
};
export const parseKeyValueMap = (raw: unknown): KeyValueEntry[] => {
  let parsed = raw;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const entries = Object.entries(parsed) as KeyValueEntry[];
    if (entries.length > 0) {
      return entries;
    }
  }
  return [];
};
export const getDestinationInitialValues = (destination_type?: OutpostDestinationTypeMetadata, destination?: OutpostDestination): DestinationFormValues => ({
  type: destination?.type ?? '',
  ...getDestinationTopicsInitialValues(destination?.topics ?? ['*']),
  config: destination_type ? destination_type.config_fields.reduce((acc, field) => {
    if (field.type === 'key_value_map') {
      acc[field.key] = parseKeyValueMap(destination?.config?.[field.key]);
    } else {
      acc[field.key] = String(destination?.config?.[field.key] ?? field.default ?? '');
    }
    return acc;
  }, {} as Record<string, string | KeyValueEntry[]>) : {},
  credentials: destination_type ? destination_type.credential_fields.reduce((acc, field) => {
    acc[field.key] = String(destination?.credentials?.[field.key] ?? '');
    return acc;
  }, {} as Record<string, string>) : {}
});
export const getCreateDestinationInitialValues = ({
  destination_type,
  topics = ['*'],
  destination_url,
  default_destination_url
}: {
  destination_type?: OutpostDestinationTypeMetadata;
  topics?: string[];
  destination_url?: string;
  default_destination_url?: string;
}): DestinationFormValues => {
  const base_values = getDestinationInitialValues(destination_type);
  const topic_values = getDestinationTopicsInitialValues(topics);
  return {
    ...base_values,
    ...topic_values,
    type: destination_type?.type ?? '',
    config: {
      ...base_values.config,
      ...(destination_type?.type === 'webhook' ? {
        url: destination_url || default_destination_url || ''
      } : {})
    }
  };
};
export const getCreateDestinationInput = ({
  destination_types,
  values
}: {
  destination_types: OutpostDestinationTypeMetadata[];
  values: DestinationFormValues;
}) => serializeDestinationInput({
  destination_type: destination_types.find(destination_type => destination_type.type === values.type),
  values
}) as OutpostDestinationCreateInput;
export const submitCreateDestinationForm = ({
  HookdeckAPI,
  tenant_id,
  destination_types,
  on_success,
  on_error
}: {
  HookdeckAPI: HookdeckApiLike;
  tenant_id: string;
  destination_types: OutpostDestinationTypeMetadata[];
  on_success?: () => void | Promise<void>;
  on_error?: (error_message?: string) => void;
}) => async (values: DestinationFormValues, {
  setSubmitting
}: FormikHelpers<DestinationFormValues>) => {
  try {
    const create_data = getCreateDestinationInput({
      destination_types,
      values
    });
    await HookdeckAPI.outpost.destinations.create(tenant_id, create_data);
    await on_success?.();
  } catch (error: unknown) {
    on_error?.(getOutpostErrorMessage(error));
  } finally {
    setSubmitting(false);
  }
};
export const noopOutpostFormSubmit = () => Promise.resolve();
export const serializeDestinationInput = ({
  destination_type,
  values,
  editing_fields = {},
  include_type = true
}: {
  destination_type?: OutpostDestinationTypeMetadata;
  values: DestinationFormValues | DestinationConfigFormValues;
  editing_fields?: Record<string, boolean>;
  include_type?: boolean;
}): OutpostDestinationCreateInput | OutpostDestinationUpdateInput => {
  const base_input: OutpostDestinationCreateInput | OutpostDestinationUpdateInput = {
    config: {}
  };
  const config_input = base_input.config ?? {};
  base_input.config = config_input;
  if (include_type && 'type' in values) {
    (base_input as OutpostDestinationCreateInput).type = values.type;
    (base_input as OutpostDestinationCreateInput).topics = values.all_topics ? '*' : values.topics;
  }
  if (!destination_type) {
    return base_input;
  }
  for (const field of destination_type.config_fields) {
    if (field.type === 'key_value_map') {
      const raw_entries = values.config[field.key];
      if (Array.isArray(raw_entries)) {
        const mapped_entries = raw_entries.reduce((acc, [key, value]) => {
          if (key.trim()) {
            acc[key.trim()] = value;
          }
          return acc;
        }, {} as Record<string, string>);
        if (Object.keys(mapped_entries).length > 0 || !include_type) {
          config_input[field.key] = mapped_entries;
        }
      }
      continue;
    }
    if (field.sensitive && !include_type && !editing_fields[`config.${field.key}`]) {
      continue;
    }
    const raw_value = values.config[field.key];
    if (raw_value !== undefined && raw_value !== '') {
      config_input[field.key] = raw_value;
    }
  }
  const credentials = destination_type.credential_fields.reduce((acc, field) => {
    const raw_value = values.credentials[field.key];
    const is_editing = editing_fields[`credentials.${field.key}`];
    const should_include = include_type ? raw_value !== undefined && raw_value !== '' : is_editing && !!String(raw_value).trim();
    if (should_include) {
      acc[field.key] = raw_value;
    }
    return acc;
  }, {} as Record<string, string>);
  if (Object.keys(credentials).length > 0) {
    base_input.credentials = credentials;
  }
  return base_input;
};
export const DestinationTopicsFields = t0 => {
  const $ = _c(6);
  const {
    topic_options,
    form_values
  } = t0;
  if (topic_options.length === 0) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Div m={{
      b: 4
    }} flex={{
      align: "center",
      gap: 2
    }}><SwitchInput name="all_topics" /><Text semi={true}>Subscribe to all topics</Text></Div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== form_values.all_topics || $[2] !== topic_options) {
    t2 = !form_values.all_topics && <MultiSelectInput label="Subscribed topics" name="topics" initial_options={topic_options} search_placeholder="Search topics" />;
    $[1] = form_values.all_topics;
    $[2] = topic_options;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
export const DestinationCreateFields = t0 => {
  const $ = _c(18);
  const {
    destination_types,
    topic_options
  } = t0;
  const {
    values
  } = useFormikContext();
  let t1;
  if ($[0] !== destination_types || $[1] !== values) {
    let t2;
    if ($[3] !== values) {
      t2 = destination_type => destination_type.type === values.type;
      $[3] = values;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = destination_types.find(t2);
    $[0] = destination_types;
    $[1] = values;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selected_type = t1;
  let t2;
  if ($[5] !== destination_types) {
    t2 = destination_types.map(_temp);
    $[5] = destination_types;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const type_options = t2;
  let t3;
  if ($[7] !== type_options) {
    t3 = <SelectInput name="type" label="Destination Type" options={type_options} placeholder="Select a destination type" required={true} block={true} />;
    $[7] = type_options;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== topic_options || $[10] !== values) {
    t4 = <DestinationTopicsFields topic_options={topic_options} form_values={values} />;
    $[9] = topic_options;
    $[10] = values;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== selected_type) {
    t5 = selected_type ? <DestinationConfigFields destination_type={selected_type} /> : null;
    $[12] = selected_type;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t6 = <>{t3}{t4}{t5}</>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
export const DestinationConfigFields = t0 => {
  const $ = _c(37);
  const {
    destination_type,
    mode: t1,
    editing_fields: t2,
    setEditingFields,
    destination
  } = t0;
  const mode = t1 === undefined ? "create" : t1;
  let t3;
  if ($[0] !== t2) {
    t3 = t2 === undefined ? {} : t2;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const editing_fields = t3;
  const {
    setFieldValue
  } = useFormikContext();
  const is_edit_mode = mode === "edit";
  let t4;
  if ($[2] !== destination?.config || $[3] !== destination_type.config_fields || $[4] !== editing_fields || $[5] !== is_edit_mode || $[6] !== setEditingFields || $[7] !== setFieldValue) {
    let t5;
    if ($[9] !== destination?.config || $[10] !== editing_fields || $[11] !== is_edit_mode || $[12] !== setEditingFields || $[13] !== setFieldValue) {
      t5 = field_0 => {
        const field_path = `config.${field_0.key}`;
        const is_editing = editing_fields[field_path];
        if (is_edit_mode && field_0.sensitive) {
          return <Div key={field_0.key} flex={{
            align: "flex-end",
            gap: 2
          }} m={{
            b: 2
          }}><Div flex={{
              grow: true
            }}><TextInput name={field_path} label={field_0.label} help={field_0.description} required={is_editing ? field_0.required : false} type={is_editing ? "password" : "text"} disabled={!is_editing} validate={is_editing && field_0.pattern ? validateField(field_0) : undefined} m={0} mono={true} /></Div><Button outline={true} icon={is_editing ? "close" : "edit"} onClick={() => {
              if (!setEditingFields) {
                return;
              }
              if (is_editing) {
                setFieldValue(field_path, String(destination?.config?.[field_0.key] ?? field_0.default ?? ""));
              } else {
                setFieldValue(field_path, "");
              }
              setEditingFields(previous => ({
                ...previous,
                [field_path]: !is_editing
              }));
            }} /></Div>;
        }
        return <TextInput key={field_0.key} name={field_path} label={field_0.label} help={field_0.description} required={field_0.required} type={field_0.sensitive ? "password" : "text"} validate={field_0.pattern ? validateField(field_0) : undefined} mono={true} />;
      };
      $[9] = destination?.config;
      $[10] = editing_fields;
      $[11] = is_edit_mode;
      $[12] = setEditingFields;
      $[13] = setFieldValue;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    t4 = destination_type.config_fields.filter(_temp2).map(t5);
    $[2] = destination?.config;
    $[3] = destination_type.config_fields;
    $[4] = editing_fields;
    $[5] = is_edit_mode;
    $[6] = setEditingFields;
    $[7] = setFieldValue;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[15] !== destination_type.config_fields) {
    t5 = destination_type.config_fields.filter(_temp3).map(_temp4);
    $[15] = destination_type.config_fields;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== destination_type) {
    t6 = destination_type.credential_fields.length > 0 && <SetupLinkField destination_type={destination_type} />;
    $[17] = destination_type;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== destination?.credentials || $[20] !== destination_type.credential_fields || $[21] !== editing_fields || $[22] !== is_edit_mode || $[23] !== setEditingFields || $[24] !== setFieldValue) {
    let t8;
    if ($[26] !== destination?.credentials || $[27] !== editing_fields || $[28] !== is_edit_mode || $[29] !== setEditingFields || $[30] !== setFieldValue) {
      t8 = field_3 => {
        const field_path_0 = `credentials.${field_3.key}`;
        const is_editing_0 = editing_fields[field_path_0];
        if (is_edit_mode) {
          return <Div key={field_3.key} flex={{
            align: "center",
            gap: 2
          }} m={{
            b: 2
          }}><TextInput name={field_path_0} w={100} label={field_3.label} help={field_3.description} placeholder={is_editing_0 ? "Enter new value" : ""} type={is_editing_0 ? "password" : "text"} disabled={!is_editing_0} validate={is_editing_0 && field_3.pattern ? validateField(field_3) : undefined} m={0} mono={true} /><Button outline={true} large={true} icon={is_editing_0 ? "close" : "edit"} onClick={() => {
              if (!setEditingFields) {
                return;
              }
              if (is_editing_0) {
                setFieldValue(field_path_0, String(destination?.credentials?.[field_3.key] ?? ""));
              } else {
                setFieldValue(field_path_0, "");
              }
              setEditingFields(previous_0 => ({
                ...previous_0,
                [field_path_0]: !is_editing_0
              }));
            }} /></Div>;
        }
        return <TextInput key={field_3.key} name={field_path_0} label={field_3.label} help={field_3.description} required={field_3.required} type="password" validate={field_3.pattern ? validateField(field_3) : undefined} mono={true} />;
      };
      $[26] = destination?.credentials;
      $[27] = editing_fields;
      $[28] = is_edit_mode;
      $[29] = setEditingFields;
      $[30] = setFieldValue;
      $[31] = t8;
    } else {
      t8 = $[31];
    }
    t7 = destination_type.credential_fields.map(t8);
    $[19] = destination?.credentials;
    $[20] = destination_type.credential_fields;
    $[21] = editing_fields;
    $[22] = is_edit_mode;
    $[23] = setEditingFields;
    $[24] = setFieldValue;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[32] !== t4 || $[33] !== t5 || $[34] !== t6 || $[35] !== t7) {
    t8 = <>{t4}{t5}{t6}{t7}</>;
    $[32] = t4;
    $[33] = t5;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  return t8;
};
function _temp(destination_type_0) {
  return {
    value: destination_type_0.type,
    label: destination_type_0.label,
    icon: <DestinationTypeIcon type={destination_type_0.type} type_meta={destination_type_0} />
  };
}
function _temp2(field) {
  return field.type !== "key_value_map";
}
function _temp3(field_1) {
  return field_1.type === "key_value_map";
}
function _temp4(field_2) {
  return <KeyValueMapField key={field_2.key} field_key={field_2.key} label={field_2.label} />;
}